import { Button, Stack } from "@chakra-ui/react";
import { useValidateVoucher } from "@/shared/core-vouchers";
import { useState, Fragment } from "react";
import MultipleInput from "@/ui/inputs/MultipleInput";
import { useAuth } from "@/shared/session";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";
import { Radio, RadioGroup } from "@/ui/chakra/radio";
import { Checkbox } from "@/ui/chakra/checkbox";
import { toast } from "@/ui/chakra/toaster";

interface ConfirmDialogValidateVoucherProps {
  isOpen: boolean;
  onClose: () => void;
  voucherId: string;
}

enum SendTo {
  Me = "me",
  Other = "other",
}

const ConfirmDialogValidateVoucher: React.FC<
  ConfirmDialogValidateVoucherProps
> = ({ isOpen, onClose, voucherId }) => {
  const { user } = useAuth();

  const [value, setValue] = useState<SendTo>(SendTo.Me);
  const [emails, setEmails] = useState<string[]>([]);
  const [isCheckEmail, setIsCheckEmail] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const { validateVoucher } = useValidateVoucher();

  const handleValidateVoucherClick = async () => {
    if (isSending || !user) return;
    setIsSending(true);
    const emailsToSend = isCheckEmail ? [...emails, user.email] : emails;
    const res = await validateVoucher({
      id: voucherId,
      emails: emailsToSend,
    });
    if (!res) {
      toast({
        title: "Error",
        description: "Error al enviar el vale",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setIsSending(false);
      return;
    }
    setIsSending(false);
    toast({
      title: "Vale enviado",
      description: "El vale ha sido enviado correctamente",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
    handleOnClose();
  };

  const handleOnSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (value === SendTo.Me) {
      handleValidateVoucherClick();
    } else {
      if (emails.length === 0) {
        toast({
          title: "Error",
          description: "Debe ingresar al menos un correo",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      handleValidateVoucherClick();
    }
  };

  const handleOnClose = () => {
    setIsSending(false);
    setValue(SendTo.Me);
    setEmails([]);
    onClose();
  };

  return (
    <DialogRoot isOpen={isOpen} onClose={handleOnClose} isCentered size="md">
      <DialogContent>
        <form onSubmit={handleOnSubmit}>
          <DialogHeader>
            <DialogTitle>Validar vale</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <RadioGroup
              colorPalette="brand"
              value={value}
              onValueChange={(e) => {
                setValue(e.value as SendTo);
                setEmails([]);
              }}
            >
              <Stack direction="column" gap={3}>
                <Radio value={SendTo.Me}>Enviar a mi correo</Radio>
                <Radio value={SendTo.Other}>Enviar a otro(s) correo(s)</Radio>
              </Stack>
            </RadioGroup>
            {value === SendTo.Other && (
              <Fragment>
                <MultipleInput
                  values={emails}
                  setValues={setEmails}
                  placeholder="Ingrese correos y presione Enter o coma"
                />
                <Checkbox
                  mt={4}
                  colorPalette="brand"
                  checked={isCheckEmail}
                  onCheckedChange={(e) => setIsCheckEmail(e.checked === true)}
                >
                  Deseo recibir una copia del vale
                </Checkbox>
              </Fragment>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={handleOnClose} type="button">
              Cerrar
            </Button>
            <Button colorPalette="brand" type="submit" loading={isSending}>
              Enviar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
};

export default ConfirmDialogValidateVoucher;
